import PackhouseReports from "../packhouse/PackhouseReports";
import ManageSubmissions from "./ManageSubmissions";
import ManageBuncherTargets from "./ManageBuncherTargets";
import ManageBunchingWindow from "./ManageBunchingWindow";
import ManageRejectionReasons from "./ManageRejectionReasons";
import FieldAdderForm from "../capture/FieldAdderForm";
// src/pages/ManagePage.js
import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getVisibleTabs, isAdmin, getUserDepartment } from "../../utils/permissions";
import ManageWorkers from "../../managepagehooks/ManageWorkers";
import ManageVarieties from "../../managepagehooks/ManageVarieties";
import ManageHarvestTypes from "../../managepagehooks/ManageHarvestTypes";
import ManageLengths from "../../managepagehooks/ManageLengths";
import ManageBunchSizes from "../../managepagehooks/ManageBunchSizes";
import ManageGHSE from "../../managepagehooks/ManageGHSE";
import useManageFieldValueAdders from "../../managepagehooks/ManageFieldValueAdders";
import "../../styles/ManagePage.css";

/* ════════════════════════════════════════
   TAB DEFINITIONS
════════════════════════════════════════ */
const ALL_TABS = [
  { key: "packhouse_reports", label: "Packhouse Reports", icon: "" },
  { key: "buncher_targets", label: "Daily Buncher Targets", icon: "" },
  { key: "bunching_window", label: "Bunching Window Time", icon: "◷" },
  { key: "rejection_reasons", label: "Rejection Reasons", icon: "≡" },
  { key: "submissions", label: "Packhouse Submissions", icon: "▤" },
  { key: "workers",   label: "Bunchers",           icon: "👤" },
  { key: "varieties", label: "Varieties",          icon: "🌿" },
  { key: "harvest",   label: "Harvest Types",      icon: "🌾" },
  { key: "lengths",   label: "Lengths",            icon: "📏" },
  { key: "bunch",     label: "Bunch Sizes",        icon: "🌸" },
  { key: "ghse",      label: "GHSE",               icon: "🏗️" },
  { key: "field",     label: "Field Value Adders", icon: "⚙️" },
];

const MANAGE_GROUPS = [
  { label: "People", keys: ["workers", "field"] },
  { label: "Production setup", keys: ["varieties", "harvest", "lengths", "bunch", "ghse"] },
  { label: "Packhouse", keys: ["packhouse_reports", "submissions"] },
  { label: "Live operations", keys: ["buncher_targets", "bunching_window"] },
  { label: "Quality", keys: ["rejection_reasons"] },
];

/* ════════════════════════════════════════
   CONFIRMATION MODAL
════════════════════════════════════════ */
const ConfirmModal = ({ config, onConfirm, onCancel }) => {
  if (!config) return null;
  const { type, title, message, confirmLabel, icon } = config;
  return (
    <div className="mp-overlay" role="dialog" aria-modal="true" aria-labelledby="mp-modal-title">
      <div className={`mp-modal mp-modal--${type}`}>
        <div className={`mp-modal-icon-wrap mp-modal-icon-wrap--${type}`}>
          <span className="mp-modal-icon">{icon}</span>
        </div>
        <div className="mp-modal-body">
          <h3 className="mp-modal-title" id="mp-modal-title">{title}</h3>
          <p className="mp-modal-message">{message}</p>
        </div>
        <div className="mp-modal-actions">
          <button className="mp-btn mp-btn--outline" onClick={onCancel}>Cancel</button>
          <button
            className={`mp-btn ${
              type === "danger"  ? "mp-btn--danger"  :
              type === "warning" ? "mp-btn--warning" :
              "mp-btn--primary"
            }`}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════
   TOAST
════════════════════════════════════════ */
const Toast = ({ toast }) => {
  if (!toast) return null;
  return (
    <div className={`mp-toast mp-toast--${toast.type}`} role="alert">
      <span className="mp-toast-icon">{toast.type === "success" ? "✓" : "✕"}</span>
      <span className="mp-toast-msg">{toast.message}</span>
    </div>
  );
};

/* ════════════════════════════════════════
   CHECKBOX GROUP
════════════════════════════════════════ */
const CheckboxGroup = ({ label, name, data = [], selected = [], onChange }) => {
  const [search, setSearch] = useState("");
  const selectedIds = selected.map((x) => (typeof x === "object" ? x.id : Number(x)));
  const filtered = data.filter((item) =>
    item.name.toLowerCase().includes(search.toLowerCase())
  );
  const allFilteredSelected =
    filtered.length > 0 && filtered.every((item) => selectedIds.includes(item.id));

  const toggle = (id) => {
    const next = selectedIds.includes(id)
      ? selectedIds.filter((v) => v !== id)
      : [...selectedIds, id];
    onChange(name, next);
  };

  const toggleAll = () => {
    const filteredIds = filtered.map((item) => item.id);
    if (allFilteredSelected) {
      onChange(name, selectedIds.filter((id) => !filteredIds.includes(id)));
    } else {
      onChange(name, Array.from(new Set([...selectedIds, ...filteredIds])));
    }
  };

  return (
    <div className="mp-field">
      <label className="mp-label">
        {label}
        {selectedIds.length > 0 && (
          <span className="mp-cb-count">{selectedIds.length}</span>
        )}
      </label>
      <div className="mp-cbgroup">
        {data.length > 4 && (
          <div className="mp-cbgroup-search">
            <svg className="mp-cbgroup-search-svg" viewBox="0 0 20 20" fill="none">
              <circle cx="8.5" cy="8.5" r="5.5" stroke="currentColor" strokeWidth="1.6" />
              <path d="M13 13l3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
            <input
              className="mp-cbgroup-search-input"
              placeholder={`Search ${label.toLowerCase()}…`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        )}
        {filtered.length > 1 && (
          <label className="mp-cb-item mp-cb-item--all">
            <span className="mp-cb-box">
              <input type="checkbox" checked={allFilteredSelected} onChange={toggleAll} />
              <span className="mp-cb-tick" />
            </span>
            <span className="mp-cb-text mp-cb-text--all">
              {allFilteredSelected ? "Deselect all" : "Select all"}
            </span>
          </label>
        )}
        <div className="mp-cbgroup-list">
          {filtered.length === 0 ? (
            <p className="mp-cb-empty">No results found</p>
          ) : (
            filtered.map((item) => {
              const checked = selectedIds.includes(item.id);
              return (
                <label key={item.id} className={`mp-cb-item ${checked ? "mp-cb-item--checked" : ""}`}>
                  <span className="mp-cb-box">
                    <input type="checkbox" checked={checked} onChange={() => toggle(item.id)} />
                    <span className="mp-cb-tick" />
                  </span>
                  <span className="mp-cb-text">{item.name}</span>
                </label>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════
   TAG PILLS
════════════════════════════════════════ */
const TagList = ({ items = [], color = "rose" }) => {
  if (!items?.length) return <span className="mp-tag-empty">—</span>;
  return (
    <div className="mp-tag-list">
      {items.map((item, i) => (
        <span key={i} className={`mp-tag mp-tag--${color}`}>{item}</span>
      ))}
    </div>
  );
};

const assignmentLabels = (profile) => (profile.assignments_detail || []).map((assignment) => {
  const level = assignment.vertical_section === "upper" ? "Upper" : assignment.vertical_section === "lower" ? "Lower" : "Single";
  const varieties = (assignment.varieties_details || []).map((name) => name.split(" (")[0]).join(", ");
  return `${assignment.bay_name} · ${level}${varieties ? ` · ${varieties}` : ""}`;
});

/* ════════════════════════════════════════
   ACCESS DENIED PANEL
════════════════════════════════════════ */
const AccessDenied = ({ department }) => (
  <div className="mp-access-denied">
    <span className="mp-access-denied-icon">🔒</span>
    <h3 className="mp-access-denied-title">Access Restricted</h3>
    <p className="mp-access-denied-msg">
      Your department{department ? ` (${department})` : ""} does not have permission to view this section.
      Contact your administrator if you believe this is an error.
    </p>
  </div>
);

/* ════════════════════════════════════════
   MANAGE PAGE
════════════════════════════════════════ */
const ManagePage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");

  const visibleTabs = getVisibleTabs(user, ALL_TABS);
  const userDept    = getUserDepartment(user);
  const userIsAdmin = isAdmin(user);

  const [activeTab, setActiveTab] = useState(() => visibleTabs.some(t => t.key === requestedTab) ? requestedTab : (visibleTabs.find(t => !["rejection_reasons", "buncher_targets", "bunching_window", "packhouse_reports"].includes(t.key))?.key ?? "workers"));
  const [editId,         setEditId]         = useState(null);
  const [profileSearch,  setProfileSearch]  = useState("");
  const [modal,          setModal]          = useState(null);
  const [toast,          setToast]          = useState(null);
  const [saving,         setSaving]         = useState(false);

  const fva = useManageFieldValueAdders();

  // If user changes or tabs change, ensure activeTab is still valid
  useEffect(() => {
    if (!visibleTabs.find((t) => t.key === activeTab)) {
      setActiveTab(visibleTabs[0]?.key ?? "workers");
    }
  }, [user]);


  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3400);
  };

  const filteredProfiles = (fva.profiles ?? []).filter((p) => {
    const q = profileSearch.toLowerCase();
    return (
      p.full_name?.toLowerCase().includes(q) ||
      p.payroll_number?.toLowerCase().includes(q)
    );
  });
  /* ── Edit ── */
  const startEdit = (profile) => {
    setModal({
      type: "info",
      icon: "✏️",
      title: "Edit Profile",
      message: `Load "${profile.full_name}" into the form for editing? Any unsaved form data will be replaced.`,
      confirmLabel: "Yes, Edit",
      onConfirm: () => {
        setEditId(profile.id);
        fva.loadForm(profile);
        setModal(null);
        setTimeout(() => document.querySelector(".fva-editor")?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
      },
    });
  };

  const cancelEdit = () => {
    setModal({
      type: "warning",
      icon: "⚠️",
      title: "Discard Changes",
      message: "Cancel editing? All unsaved changes will be lost.",
      confirmLabel: "Discard",
      onConfirm: () => {
        setEditId(null);
        fva.resetForm();
        setModal(null);
      },
    });
  };

  /* ── Save / Create ── */
  const requestSave = () => {
    const name    = fva.form.full_name?.trim();
    const payroll = fva.form.payroll_number?.trim();
    if (!name || !payroll) {
      showToast("error", "Full name and payroll number are required.");
      return;
    }
    setModal({
      type: editId ? "warning" : "info",
      icon: editId ? "💾" : "✅",
      title: editId ? "Save Changes" : "Create Profile",
      message: editId
        ? `Save updates to "${name}"? The profile will be updated immediately.`
        : `Create a new profile for "${name}" (Payroll: ${payroll})?`,
      confirmLabel: editId ? "Save Changes" : "Create Profile",
      onConfirm: () => handleSave(name),
    });
  };

  const handleSave = async (nameForToast) => {
    setModal(null);
    setSaving(true);
    const payload = {
      ...fva.prepareSubmitData(),
      full_name: fva.form.full_name,
      payroll_number: fva.form.payroll_number,
    };
    try {
      if (editId) {
        await fva.updateProfile(editId, payload);
        setEditId(null);
        showToast("success", `"${nameForToast}" updated successfully.`);
      } else {
        await fva.createProfile(payload);
        showToast("success", `"${nameForToast}" created successfully.`);
      }
      fva.resetForm();
    } catch {
      showToast("error", "Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  /* ── Delete ── */
  const requestDelete = (profile) => {
    setModal({
      type: "danger",
      icon: "🗑️",
      title: "Delete Profile",
      message: `Permanently delete "${profile.full_name}" (${profile.payroll_number})? This cannot be undone.`,
      confirmLabel: "Delete Permanently",
      onConfirm: async () => {
        setModal(null);
        try {
          await fva.deleteProfile(profile.id);
          showToast("success", `"${profile.full_name}" has been deleted.`);
        } catch {
          showToast("error", "Failed to delete profile. Please try again.");
        }
      },
    });
  };

  /* ── Reset ── */
  const requestReset = () => {
    const hasData = fva.form.full_name || fva.form.payroll_number;
    if (!hasData) { fva.resetForm(); return; }
    setModal({
      type: "warning",
      icon: "🔄",
      title: "Reset Form",
      message: "Clear all form fields? Unsaved data will be lost.",
      confirmLabel: "Reset",
      onConfirm: () => {
        setEditId(null);
        fva.resetForm();
        setModal(null);
      },
    });
  };

  /* ════════════════════════════════════════
     RENDER
  ════════════════════════════════════════ */
  return (
    <div className="mp-root">
      <ConfirmModal config={modal} onConfirm={modal?.onConfirm} onCancel={() => setModal(null)} />
      <Toast toast={toast} />

      {/* ══════════ HEADER ══════════ */}
      <header className="mp-header">
        <div className="mp-header-accent" />
        <div className="mp-header-content">
          <span className="mp-eyebrow">RVR · OPERATIONS WORKSPACE</span>
          <h1 className="mp-title">Manage operations</h1>
          <p className="mp-subtitle">Configure people, production, packhouse and quality tools in one place.
            {userIsAdmin && <span className="mp-header-role-badge">Admin</span>}
            {!userIsAdmin && userDept && (
              <span className="mp-header-role-badge mp-header-role-badge--dept">
                {userDept.charAt(0).toUpperCase() + userDept.slice(1)}
              </span>
            )}
          </p>
        </div>
        <div className="mp-header-geo" aria-hidden="true">
          <div className="mp-geo-ring mp-geo-ring--1" />
          <div className="mp-geo-ring mp-geo-ring--2" />
          <div className="mp-geo-ring mp-geo-ring--3" />
          <div className="mp-geo-dot" />
        </div>
      </header>

      {/* ══════════ TABS — only shows what user can access ══════════ */}
      {visibleTabs.length > 0 ? (
        <>
          <div className="mp-workspace">
            <nav className="mp-tabrail mp-sidebar-nav" role="tablist" aria-label="Management sections">
              <div className="mp-sidebar-heading"><span>WORKSPACE</span><strong>Manage</strong></div>
              {MANAGE_GROUPS.map((group) => {
                const tabs = group.keys.map((key) => visibleTabs.find((tab) => tab.key === key)).filter(Boolean);
                if (!tabs.length) return null;
                return <div className="mp-nav-group" key={group.label}>
                  <span className="mp-nav-group-label">{group.label}</span>
                  {tabs.map(({ key, label, icon }) => <button key={key} role="tab" aria-selected={activeTab === key} className={`mp-tab ${activeTab === key ? "mp-tab--active" : ""}`} onClick={() => setActiveTab(key)}>
                    <span className="mp-tab-icon" aria-hidden="true">{icon}</span><span className="mp-tab-label">{label}</span>
                    {activeTab === key && <span className="mp-tab-pip" />}
                  </button>)}
                </div>;
              })}
            </nav>

            {/* ══════════ CONTENT ══════════ */}
            <main className="mp-panel">

            {/* ── Field Value Adders ── */}
            {activeTab === "field" && (
              <div className="mp-section">
                <div className="mp-section-header">
                  <h2 className="mp-section-title">Field Value Adders</h2>
                  <p className="mp-section-sub">Assign farms, bays, varieties &amp; greenhouses to worker profiles</p>
                </div>

                <FieldAdderForm fva={fva} editId={editId} saving={saving} onSave={requestSave} onCancel={cancelEdit} onReset={requestReset} />

                {/* Table */}
                <div className="mp-table-section">
                  <div className="mp-table-toolbar">
                    <div className="mp-table-title-row">
                      <h3 className="mp-cards-title">All Profiles</h3>
                      <span className="mp-badge-count">{filteredProfiles.length}</span>
                      {profileSearch && fva.profiles?.length !== filteredProfiles.length && (
                        <span className="mp-table-filter-note">of {fva.profiles?.length} total</span>
                      )}
                    </div>
                    <div className="mp-table-search-wrap">
                      <svg className="mp-table-search-svg" viewBox="0 0 20 20" fill="none">
                        <circle cx="8.5" cy="8.5" r="5.5" stroke="currentColor" strokeWidth="1.6" />
                        <path d="M13 13l3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                      </svg>
                      <input
                        className="mp-table-search"
                        placeholder="Search by name or payroll…"
                        value={profileSearch}
                        onChange={(e) => setProfileSearch(e.target.value)}
                      />
                      {profileSearch && (
                        <button className="mp-table-clear" onClick={() => setProfileSearch("")}>✕</button>
                      )}
                    </div>
                  </div>

                  {fva.loading ? (
                    <div className="mp-loading">
                      <span className="mp-spinner" /><span>Loading profiles…</span>
                    </div>
                  ) : filteredProfiles.length === 0 ? (
                    <div className="mp-table-empty">
                      <span className="mp-table-empty-icon">🌿</span>
                      <p className="mp-table-empty-text">
                        {profileSearch
                          ? `No profiles match "${profileSearch}"`
                          : "No profiles yet — create one above."}
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Desktop table */}
                      <div className="mp-table-wrap">
                        <table className="mp-table">
                          <thead>
                            <tr>
                              <th className="mp-th">#</th>
                              <th className="mp-th">Name</th>
                              <th className="mp-th">Payroll No.</th>
                              <th className="mp-th">Farms</th>
                              <th className="mp-th">GHSE</th>
                              <th className="mp-th">Bays</th>
                              <th className="mp-th">Varieties</th>
                              <th className="mp-th mp-th--actions">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredProfiles.map((p, i) => (
                              <tr
                                key={p.id}
                                className={`mp-tr${editId === p.id ? " mp-tr--editing" : ""}`}
                                style={{ "--row-index": i }}
                              >
                                <td className="mp-td mp-td--num">{i + 1}</td>
                                <td className="mp-td">
                                  <div className="mp-table-name-cell">
                                    <div className="mp-table-avatar">{p.full_name?.charAt(0).toUpperCase()}</div>
                                    <span className="mp-table-name">{p.full_name}</span>
                                  </div>
                                </td>
                                <td className="mp-td">
                                  <span className="mp-table-payroll">{p.payroll_number}</span>
                                </td>
                                <td className="mp-td mp-td--tags"><TagList items={p.farms_details}     color="rose" /></td>
                                <td className="mp-td mp-td--tags"><TagList items={p.ghses_details}     color="sage" /></td>
                                <td className="mp-td mp-td--tags"><TagList items={assignmentLabels(p).length ? assignmentLabels(p) : p.bays_details} color="rose" /></td>
                                <td className="mp-td mp-td--tags"><TagList items={p.varieties_details} color="sage" /></td>
                                <td className="mp-td mp-td--actions">
                                  <div className="mp-row-actions">
                                    <button className="mp-btn-icon mp-btn-icon--edit"   onClick={() => startEdit(p)}>✏️ Edit</button>
                                    <button className="mp-btn-icon mp-btn-icon--delete" onClick={() => requestDelete(p)}>🗑</button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Mobile cards */}
                      <div className="mp-mobile-list">
                        {filteredProfiles.map((p, i) => (
                          <div
                            key={p.id}
                            className={`mp-mobile-card${editId === p.id ? " mp-mobile-card--editing" : ""}`}
                            style={{ "--row-index": i }}
                          >
                            <div className="mp-mobile-card-head">
                              <div className="mp-table-avatar">{p.full_name?.charAt(0).toUpperCase()}</div>
                              <div className="mp-mobile-card-info">
                                <p className="mp-table-name">{p.full_name}</p>
                                <span className="mp-table-payroll">{p.payroll_number}</span>
                              </div>
                              <div className="mp-row-actions mp-row-actions--mobile">
                                <button className="mp-btn-icon mp-btn-icon--edit"   onClick={() => startEdit(p)}>✏️</button>
                                <button className="mp-btn-icon mp-btn-icon--delete" onClick={() => requestDelete(p)}>🗑</button>
                              </div>
                            </div>
                            <div className="mp-mobile-card-body">
                              {[
                                { label: "Farms",     items: p.farms_details,     color: "rose" },
                                { label: "GHSE",      items: p.ghses_details,     color: "sage" },
                                { label: "Bay / level", items: assignmentLabels(p).length ? assignmentLabels(p) : p.bays_details, color: "rose" },
                                { label: "Varieties", items: p.varieties_details, color: "sage" },
                              ].filter((r) => r.items?.length).map(({ label, items, color }) => (
                                <div key={label} className="mp-mobile-row">
                                  <span className="mp-mobile-label">{label}</span>
                                  <TagList items={items} color={color} />
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {activeTab === "packhouse_reports" && <PackhouseReports />}
            {activeTab === "submissions" && userIsAdmin && <ManageSubmissions />}
            {activeTab === "buncher_targets" && <ManageBuncherTargets />}
            {activeTab === "bunching_window" && <ManageBunchingWindow />}
            {activeTab === "rejection_reasons" && <ManageRejectionReasons />}
            {activeTab === "workers"   && <div className="mp-section"><ManageWorkers /></div>}
            {activeTab === "varieties" && <div className="mp-section"><ManageVarieties /></div>}
            {activeTab === "harvest"   && <div className="mp-section"><ManageHarvestTypes /></div>}
            {activeTab === "lengths"   && <div className="mp-section"><ManageLengths /></div>}
            {activeTab === "bunch"     && <div className="mp-section"><ManageBunchSizes /></div>}
            {activeTab === "ghse"      && <div className="mp-section"><ManageGHSE /></div>}

            </main>
          </div>
        </>
      ) : (
        // No tabs visible at all — user has no department match
        <main className="mp-panel">
          <AccessDenied department={userDept} />
        </main>
      )}
    </div>
  );
};

export default ManagePage;
